import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { 
  fetchSuccessStories, 
  createStory, 
  updateStory, 
  deleteStory, 
  selectAllStories, 
  selectStoriesStatus, 
  selectStoriesError 
} from '../../data/successStoriesSlice';

import { baseURL } from '../../services/APIService';
const AdminSuccessStories = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Get stories from Redux store
  const stories = useSelector(selectAllStories);
  const status = useSelector(selectStoriesStatus);
  const error = useSelector(selectStoriesError);
  
  // Component state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [storyToEdit, setStoryToEdit] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    details: '',
    image: null,
    imagePreview: null
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  
  // Fetch stories on component mount
  useEffect(() => {
    dispatch(fetchSuccessStories());
  }, [dispatch]);
  
  // Debug: Log stories when they change
  useEffect(() => {
  }, [stories]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };
  
  // Reset form data
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      details: '',
      image: null,
      imagePreview: null
    });
  };
  
  // Open edit modal with story data
  const openEditModal = (story) => {
    setStoryToEdit(story);
    
    // Get the correct image URL
    let imageUrl = story.image;
    if (!imageUrl && story.hasImage) {
      imageUrl = `${baseURL}/stories/image/${story.id}`;
    }
    
  
    
    setFormData({
      name: story.name || story.title || '',
      description: story.description || story.content || '',
      details: story.details || '',
      image: null,
      imagePreview: imageUrl
    });
    setShowEditModal(true);
  };
  
  // Close modals
  const closeAddModal = () => {
    resetForm();
    setShowAddModal(false);
  };
  
  const closeEditModal = () => {
    resetForm();
    setStoryToEdit(null);
    setShowEditModal(false);
  };
  
  // Handle story submission (create new)
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    
 
    
    // Create form data for submission including image
    const storyData = new FormData();
    
    // Add the title and content fields exactly as the API expects
    storyData.append('title', formData.name);
    storyData.append('content', formData.description);
    
    // Only append details if it exists and is not empty
    if (formData.details && formData.details.trim() !== '') {
      storyData.append('details', formData.details);
    }
    
    // Make sure image is included (it's required for new stories)
    if (formData.image instanceof File) {
      storyData.append('image', formData.image);
    } else {
      console.error('No image selected for new story');
      // Show an error to the user
      alert('Please select an image for the success story');
      return;
    }
    
   
    
    try {
      await dispatch(createStory(storyData)).unwrap();
      closeAddModal();
      // Refresh stories after adding a new one
      dispatch(fetchSuccessStories());
    } catch (err) {
      console.error('Failed to create story:', err);
      alert('Failed to create story: ' + (typeof err === 'string' ? err : 'Unknown error'));
    }
  };
  
  // Handle story update
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!storyToEdit) {
      console.error('No story selected for update');
      return;
    }
    
    // Create form data for submission including image
    const storyData = new FormData();
    
    // Add the title and content fields exactly as the API expects
    storyData.append('title', formData.name);
    storyData.append('content', formData.description);
    
    // Only append details if it exists and is not empty
    if (formData.details && formData.details.trim() !== '') {
      storyData.append('details', formData.details);
    }
    
    // Include the image only if a new one was selected
    if (formData.image instanceof File) {
      storyData.append('image', formData.image);
    } 
    
   
    
    try {
      await dispatch(updateStory({ id: storyToEdit.id, storyData })).unwrap();
      closeEditModal();
      // Refresh stories after update
      dispatch(fetchSuccessStories());
    } catch (err) {
      console.error('Failed to update story:', err);
      alert('Failed to update story: ' + (typeof err === 'string' ? err : 'Unknown error'));
    }
  };
  
  // Handle story deletion
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;
    
    try {
      await dispatch(deleteStory(deleteConfirmation)).unwrap();
      setDeleteConfirmation(null);
      // Refresh stories list after deletion
      dispatch(fetchSuccessStories());
    } catch (err) {
      console.error('Failed to delete story:', err);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('manage_success_stories')}</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('add_success_story')}
        </button>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {typeof error === 'object' ? 'An error occurred. Please try again.' : error}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading state */}
      {status === 'loading' && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      
      {/* Success stories table */}
      {status !== 'loading' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {stories.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_success_stories')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('add_success_story_to_get_started')}</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('add_success_story')}
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('image')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('name')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('description')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('created')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stories.map(story => (
                    <tr key={story.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {story.hasImage || story.image ? (
                          <img 
                            src={story.image || `${baseURL}/stories/image/${story.id}`}
                            alt={story.name || story.title} 
                            className="h-12 w-16 object-cover rounded" 
                            onError={(e) => {
                              console.log('Image failed to load:', e.target.src);
                              e.target.onerror = null;
                              e.target.src = '/assets/placeholder-image.jpg';
                            }}
                          />
                        ) : (
                          <div className="h-12 w-16 bg-gray-200 rounded flex items-center justify-center">
                            <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{story.name || story.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 line-clamp-2 max-w-xs">{story.description || story.content}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatDate(story.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => openEditModal(story)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title={t('edit')}
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteConfirmation(story.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('delete')}
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Add Story Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog" 
              aria-modal="true" 
              aria-labelledby="modal-headline"
            >
              <form onSubmit={handleAddSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                        {t('add_success_story')}
                      </h3>
                      <div className="mt-4 space-y-4">
                        {/* Image Upload */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('image')}
                          </label>
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-20 w-20 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                              {formData.imagePreview ? (
                                <img 
                                  src={formData.imagePreview} 
                                  alt="Preview" 
                                  className="h-full w-full object-cover" 
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="ml-5">
                              <div className="relative rounded-md shadow-sm">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageChange}
                                  id="story-image"
                                  className="sr-only"
                                />
                                <label
                                  htmlFor="story-image"
                                  className="py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer inline-block"
                                >
                                  {t('choose_image')}
                                </label>
                              </div>
                              <p className="mt-1 text-xs text-gray-500">{t('image_size_recommendation')}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Name */}
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('name')}
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            required
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            value={formData.name}
                            onChange={handleChange}
                          />
                        </div>
                        
                        {/* Description */}
                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('description')}
                          </label>
                          <textarea
                            id="description"
                            name="description"
                            rows="3"
                            required
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            value={formData.description}
                            onChange={handleChange}
                          ></textarea>
                        </div>
                        
                        {/* Details (optional) */}
                        <div>
                          <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('details')} ({t('optional')})
                          </label>
                          <textarea
                            id="details"
                            name="details"
                            rows="4"
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            value={formData.details}
                            onChange={handleChange}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('save')}
                  </button>
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Story Modal */}
      {showEditModal && storyToEdit && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog" 
              aria-modal="true" 
              aria-labelledby="modal-headline"
            >
              <form onSubmit={handleEditSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                        {t('edit_success_story')}
                      </h3>
                      <div className="mt-4 space-y-4">
                        {/* Image Upload */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('image')}
                          </label>
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-20 w-20 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                              {formData.imagePreview ? (
                                <img 
                                  src={formData.imagePreview} 
                                  alt="Preview" 
                                  className="h-full w-full object-cover" 
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="ml-5">
                              <div className="relative rounded-md shadow-sm">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleImageChange}
                                  id="edit-story-image"
                                  className="sr-only"
                                />
                                <label
                                  htmlFor="edit-story-image"
                                  className="py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer inline-block"
                                >
                                  {t('choose_new_image')}
                                </label>
                              </div>
                              <p className="mt-1 text-xs text-gray-500">{t('leave_empty_to_keep_current')}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Name */}
                        <div>
                          <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('name')}
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="edit-name"
                            required
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            value={formData.name}
                            onChange={handleChange}
                          />
                        </div>
                        
                        {/* Description */}
                        <div>
                          <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('description')}
                          </label>
                          <textarea
                            id="edit-description"
                            name="description"
                            rows="3"
                            required
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            value={formData.description}
                            onChange={handleChange}
                          ></textarea>
                        </div>
                        
                        {/* Details (optional) */}
                        <div>
                          <label htmlFor="edit-details" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('details')} ({t('optional')})
                          </label>
                          <textarea
                            id="edit-details"
                            name="details"
                            rows="4"
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            value={formData.details}
                            onChange={handleChange}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('update')}
                  </button>
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog" 
              aria-modal="true" 
              aria-labelledby="modal-headline"
            >
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                      {t('delete_success_story')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t('delete_success_story_confirmation')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('delete')}
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmation(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSuccessStories;
